<template>
    <v-dialog v-model="show" max-width="600px">
        <v-card>
            <v-card-title>
                <span class="headline">
                    {{ $t("licenses.devices") }}
                </span>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid" lazy-validation>
                    <v-container>
                        <v-row>
                            <v-col 
                                v-for="(item, index) in deviceData.devices" 
                                :key="'device-' + index"
                                cols="12" 
                                sm="6" 
                                md="4"
                            >
                                <v-text-field
                                    v-model="deviceCodes[index]"
                                    :label="`${$t('licenses.oculus')} ${index + 1}`"
                                    :error-messages="getFieldErrors(index)"
                                    :rules="[(v) => rules.noDuplicates(v, index), rules.validDevice]"
                                    clearable
                                    @input="validateField(index)"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon small color="primary">
                                            mdi-safety-goggles
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Alertas de validación -->
                        <v-row v-if="validationAlerts.length > 0">
                            <v-col cols="12">
                                <v-alert
                                    v-for="(alert, index) in validationAlerts"
                                    :key="`alert-${index}`"
                                    type="warning"
                                    outlined
                                    dense
                                    class="mb-2"
                                    dismissible
                                >
                                    {{ alert }}
                                </v-alert>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-alert
                                    type="info"
                                    outlined
                                    dense
                                    class="mb-0"
                                >
                                    {{ deviceData.name }}
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn 
                    color="primary" 
                    dark 
                    @click="submit" 
                    :disabled="isSubmitting || !valid"
                    :loading="isSubmitting"
                >
                    {{ $t("general.save") }}
                </v-btn>
                <v-btn @click.stop="close">
                    {{ $t("general.cancel") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: "LicenceDeviceForm",
    
    props: {
        deviceData: {
            type: Object,
            default: () => ({})
        },
        visible: Boolean,
    },
    
    data() {
        return {
            valid: true,
            oculusDevices: [], 
            isSubmitting: false,
            deviceCodes: [],
            validationAlerts: [],
            rules: {
                noDuplicates: (value, currentIndex) => {
                    if (!value) return true;
                    const duplicateIndex = this.deviceCodes.findIndex((code, idx) => 
                        idx !== currentIndex && code && code.toLowerCase() === value.toLowerCase()
                    );
                    return duplicateIndex === -1 || this.$t('licenses.duplicate_device_code');
                },
                validDevice: value => {
                    if (!value) return true;
                    const existsInAvailable = this.oculusDevices.some(device => 
                        device.code && device.code.toLowerCase() === value.toLowerCase()
                    );
                    const existsInOriginal = this.deviceData.codes && this.deviceData.codes.some(codeObj => 
                        codeObj.code && codeObj.code.toLowerCase() === value.toLowerCase()
                    );
                    return (existsInAvailable || existsInOriginal) || this.$t('licenses.invalid_device_code');
                }
            }
        };
    },

    computed: {
        show: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.close();
                }
            },
        }
    },

    watch: {
        visible(val) {
            if (val) {
                this.resetFormData(); 
                this.getOculus(); 
            } else {
                this.resetFormData(); 
            }
        },
    },

    methods: {
        async getOculus() {
            try {
                console.log('Loading oculus devices...');
                const response = await axios.get("/oculusDeviceUnassigned", {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                
                this.oculusDevices = response.data || [];
                console.log('Oculus devices loaded:', this.oculusDevices);

                if (this.visible && this.deviceData) {
                    this.initializeFormData();
                }
                
            } catch (error) {    
                console.error("Error loading oculus devices:", error.response?.data || error);
                this.$emit("snackMessage", this.$t("general.error_loading_clients"), "error");
                this.oculusDevices = [];
            }
        },

        initializeFormData() {
            console.log('Initializing form data...');
            console.log('Device data:', this.deviceData);
            
            if (!this.deviceData || !this.deviceData.devices) {
                console.warn('Device data not available yet');
                return;
            }
     
            this.deviceCodes = new Array(this.deviceData.devices).fill('');
            
            if (this.deviceData.codes && this.deviceData.codes.length > 0) {
                this.deviceData.codes.forEach((codeObj, index) => {
                    if (index < this.deviceCodes.length) {
                        this.$set(this.deviceCodes, index, codeObj.code || '');
                    }
                });
            }
            
            console.log('Initialized deviceCodes:', this.deviceCodes);
        },

        validateField(index) {
            this.validationAlerts = [];
            
            const currentValue = this.deviceCodes[index];
            if (!currentValue) return;

            // Verificar duplicados
            const duplicates = this.deviceCodes.filter((code, idx) => 
                idx !== index && code && code.toLowerCase() === currentValue.toLowerCase()
            );
            
            if (duplicates.length > 0) {
                this.validationAlerts.push(
                    `${this.$t('licenses.oculus')} ${index + 1}: ${this.$t('licenses.duplicate_device_code')}`
                );
            }

            // Verificar si el dispositivo existe
            const deviceExists = this.oculusDevices.some(device => 
                device.code && device.code.toLowerCase() === currentValue.toLowerCase()
            ) || (this.deviceData.codes && this.deviceData.codes.some(codeObj => 
                codeObj.code && codeObj.code.toLowerCase() === currentValue.toLowerCase()
            ));
            
            if (!deviceExists) {
                this.validationAlerts.push(
                    `${this.$t('licenses.oculus')} ${index + 1}: '${currentValue}' ${this.$t('licenses.not_found')}`
                );
            }
        },

        getFieldErrors(index) {
            const errors = [];
            const currentValue = this.deviceCodes[index];
            
            if (!currentValue) return errors;

            // Check for duplicates
            const duplicateIndex = this.deviceCodes.findIndex((code, idx) => 
                idx !== index && code && code.toLowerCase() === currentValue.toLowerCase()
            );
            
            if (duplicateIndex !== -1) {
                errors.push(this.$t('licenses.duplicate_device_code'));
            }

            // Check if device exists
            const deviceExists = this.oculusDevices.some(device => 
                device.code && device.code.toLowerCase() === currentValue.toLowerCase()
            ) || (this.deviceData.codes && this.deviceData.codes.some(codeObj => 
                codeObj.code && codeObj.code.toLowerCase() === currentValue.toLowerCase()
            ));
            
            if (!deviceExists) {
                errors.push(this.$t('licenses.invalid_device_code'));
            }

            return errors;
        },

        validateAllFields() {
            this.validationAlerts = [];
            let isValid = true;
            
            for (let i = 0; i < this.deviceCodes.length; i++) {
                const code = this.deviceCodes[i];
                if (!code) continue;

                // Check duplicates
                const duplicates = this.deviceCodes.filter((c, idx) => 
                    idx !== i && c && c.toLowerCase() === code.toLowerCase()
                );
                
                if (duplicates.length > 0) {
                    this.validationAlerts.push(
                        `${this.$t('licenses.oculus')} ${i + 1}: Código duplicado`
                    );
                    isValid = false;
                }

                // Check if device exists
                const deviceExists = this.oculusDevices.some(device => 
                    device.code && device.code.toLowerCase() === code.toLowerCase()
                ) || (this.deviceData.codes && this.deviceData.codes.some(codeObj => 
                    codeObj.code && codeObj.code.toLowerCase() === code.toLowerCase()
                ));
                
                if (!deviceExists) {
                    this.validationAlerts.push(
                        `${this.$t('licenses.oculus')} ${i + 1}: Dispositivo "${code}" no encontrado`
                    );
                    isValid = false;
                }
            }

            return isValid;
        },

        async submit() {
            if (!this.$refs.form.validate()) return;
            if (!this.validateAllFields()) return;

            try {
                this.isSubmitting = true;
                
                // Convert codes to device IDs
                const selectedDeviceIds = [];
                
                for (const code of this.deviceCodes) {
                    if (code) {
                        // First try to find in available devices
                        let device = this.oculusDevices.find(d => 
                            d.code && d.code.toLowerCase() === code.toLowerCase()
                        );
                        
                        // If not found, try to find in original assigned devices
                        if (!device && this.deviceData.codes) {
                            const originalDevice = this.deviceData.codes.find(codeObj => 
                                codeObj.code && codeObj.code.toLowerCase() === code.toLowerCase()
                            );
                            if (originalDevice) {
                                device = { id: originalDevice.id, code: originalDevice.code };
                            }
                        }
                        
                        if (device) {
                            selectedDeviceIds.push(device.id);
                        }
                    }
                }
                
                const payload = {
                    oculus_devices: selectedDeviceIds
                };
            
                console.log('Submitting payload:', payload);
                
                const response = await axios.put(`/license/code/${this.deviceData.id}`, payload, {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                
                console.log('Response:', response.data);
                this.$emit("snackMessage", this.$t("general.save"), "success");
                this.$emit("refresh");
                
                await this.getOculus();
                this.close();
                
            } catch (error) {
                console.error("Error saving device codes:", error);
                const errorMessage = error.response?.data?.message || this.$t("general.error");
                this.$emit("snackMessage", errorMessage, "error");
                
                // Add server error to validation alerts
                this.validationAlerts.push(errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },

        close() {
            this.$emit("close");
            this.resetFormData();
        },

        resetFormData() {
            console.log('Resetting form data...');
            this.deviceCodes = []; 
            this.validationAlerts = [];
            this.valid = true;
            this.isSubmitting = false;
            
            if (this.$refs.form) {
                this.$refs.form.reset();
                this.$refs.form.resetValidation();
            }
        },
    },

    created() {
    
    }
};
</script>